import React, { useEffect, useState } from 'react';
import { Trophy, ShoppingBag, Percent, DollarSign, Star } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { MyResults, Reputation, fetchMyResults, fetchReputation, formatAvg } from '@/lib/contractorRatings';

/**
 * "My results" — the contractor's own performance. Numbers come from the
 * mkt_contractor_outcome_stats RPC (non-admins only ever receive their own row)
 * plus the trigger-maintained rating counters on their profile.
 */
const MyResultsCard: React.FC = () => {
  const { lang } = useI18n();
  const es = lang === 'es';
  const { profile } = useAuth();
  const [res, setRes] = useState<MyResults | null>(null);
  const [rep, setRep] = useState<Reputation | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!profile?.id) return;
    let alive = true;
    fetchMyResults(profile.id)
      .then((r) => alive && setRes(r))
      .catch(() => alive && setFailed(true));
    fetchReputation([profile.id]).then((m) => alive && setRep(m.get(profile.id) || null)).catch(() => null);
    return () => {
      alive = false;
    };
  }, [profile?.id]);

  if (failed || !profile?.id) return null;

  const money = (n: number) => `$${Number(n || 0).toLocaleString(es ? 'es-US' : 'en-US')}`;
  const tiles = [
    { icon: ShoppingBag, label: es ? 'Leads comprados' : 'Leads bought', value: res ? res.leads_bought.toLocaleString() : '…', tone: 'text-slate-900' },
    { icon: Trophy, label: es ? 'Trabajos ganados' : 'Jobs won', value: res ? res.jobs_won.toLocaleString() : '…', tone: 'text-emerald-700' },
    { icon: Percent, label: es ? 'Tasa de éxito' : 'Win rate', value: res ? (res.win_rate_pct == null ? '—' : `${res.win_rate_pct}%`) : '…', tone: 'text-orange-600' },
    { icon: DollarSign, label: es ? 'Valor de trabajos terminados' : 'Completed job value', value: res ? money(res.completed_job_value) : '…', tone: 'text-slate-900' },
  ];

  return (
    <section className="mb-8 rounded-2xl border border-slate-200 bg-white p-5" aria-labelledby="my-results-title">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="my-results-title" className="flex items-center gap-2 text-lg font-extrabold text-slate-900">
          <Trophy className="h-5 w-5 text-amber-500" /> {es ? 'Mis resultados' : 'My results'}
        </h2>
        {rep && rep.rating_count > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-bold text-amber-800 ring-1 ring-amber-200">
            <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" /> {formatAvg(rep.avg_rating)} · {rep.rating_count}{' '}
            {es ? (rep.rating_count === 1 ? 'calificación' : 'calificaciones') : rep.rating_count === 1 ? 'rating' : 'ratings'}
          </span>
        )}
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-xl bg-slate-50 p-3">
            <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-400">
              <t.icon className="h-3.5 w-3.5" /> {t.label}
            </p>
            <p className={`mt-1 text-xl font-extrabold ${t.tone}`}>{t.value}</p>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-slate-500">
        {es
          ? 'La tasa de éxito cuenta los proyectos que compraste y que el propietario cerró como contratado o terminado. Se actualiza cuando los propietarios registran el resultado.'
          : 'Win rate counts projects you bought that the homeowner closed as hired or completed. It updates as homeowners record outcomes.'}
      </p>
    </section>
  );
};

export default MyResultsCard;
